/**
 * Cloudscape CollectionPreferences + localStorage column-visibility reconciliation.
 *
 * THE BUG THIS FIXES — Cloudscape's `visibleContent` persists only a POSITIVE
 * allowlist ("which columns are visible"), never "which columns the user hid".
 * So "id X is absent from the stored array" has two indistinguishable causes:
 *   A) X existed and the user un-checked it in the gear  → keep it hidden
 *   B) X didn't exist yet when they saved (added later)  → show it by default
 * The naive load (filter-only: keep stored ids, drop unknown ones) treats every
 * newly-added column as case A, so a NEW column defaults to HIDDEN for anyone who
 * used the table before it existed.
 *
 * THE FIX — snapshot the columns that existed at save time (`knownColumns`). On
 * load, a column that is in the current set but was NOT in that snapshot is case B
 * (new) → union it into the visible set; a column absent from the visible set but
 * present in the snapshot is case A (user hid it) → leave it hidden. This is
 * self-maintaining: adding or removing a column later needs NO change here.
 *
 * MIGRATION — prefs saved BEFORE this fix carry no `knownColumns`. For those we
 * fall back to a caller-supplied `legacyKnownColumns` baseline: the frozen column
 * set as it was the moment this fix shipped. A column not in that baseline is
 * genuinely new (show it); a column in the baseline that the user dropped stays
 * hidden — so the migration re-shows ONLY真·new columns, never resurrecting a
 * column the user intentionally hid. `legacyKnownColumns` is set once and never
 * changed again (subsequent saves write a fresh `knownColumns` snapshot).
 */

export interface ReconcileVisibleContentArgs {
  /** The stored `visibleContent` array (may be undefined / not-an-array). */
  storedVisible: readonly string[] | undefined;
  /** The stored `knownColumns` snapshot (absent for pre-fix prefs). */
  storedKnown: readonly string[] | undefined;
  /** The CURRENT set of hideable column ids (source of truth for what exists). */
  hideableColumns: readonly string[];
  /** One-time frozen baseline: hideable columns as of when this fix shipped.
   *  Used only when `storedKnown` is absent (pre-fix prefs). */
  legacyKnownColumns: readonly string[];
  /** Fallback when the stored visible set is empty/absent (never zero columns). */
  defaultVisible: readonly string[];
}

/**
 * Reconcile a stored visible-column set against the current columns, defaulting
 * newly-added columns to visible while preserving intentional hides.
 * Order is irrelevant to callers (they consume the result as a Set), but stored
 * ids keep their order and newly-added ids are appended.
 */
export function reconcileVisibleContent({
  storedVisible,
  storedKnown,
  hideableColumns,
  legacyKnownColumns,
  defaultVisible,
}: ReconcileVisibleContentArgs): string[] {
  // Drop ids no longer in the schema (删列 safety) — this alone is what made the
  // old code safe for REMOVED columns; we keep it.
  const known = Array.isArray(storedVisible)
    ? storedVisible.filter((id) => hideableColumns.includes(id))
    : undefined;

  // Empty / unknown → all columns (never render zero columns).
  if (!known || known.length === 0) return [...defaultVisible];

  // What existed when the user last saved: their own snapshot if present, else
  // the frozen pre-fix baseline. A missing snapshot means "saved before the fix".
  const knownBefore = Array.isArray(storedKnown) ? storedKnown : legacyKnownColumns;

  // Columns that exist NOW but did NOT when they saved → genuinely new → show.
  const newlyAdded = hideableColumns.filter(
    (id) => !knownBefore.includes(id) && !known.includes(id),
  );

  return [...known, ...newlyAdded];
}
